import { TIME_FRAME } from '@models/common.model'
import { LeakManagement } from '@models/leak.model'
import { WaterAccountManagement } from '@models/waterAccount.model'
import { Helpers } from '@utils/helper'
import { LodashUtils } from '@utils/lodash'
import { AxiosResponse } from 'axios'
import dayjs from 'dayjs'
import { protocol } from './index'

const URL_POST_NEW_WATER_ACCOUNT = '/api/waterAccount/addNew'
export const postNewAccount = (payload: any) => {
  return protocol.post(URL_POST_NEW_WATER_ACCOUNT, payload)
}

const URL_POST_SET_WATER_ACCOUNT_ORDER = '/api/waterAccount/setOrder'
export const setWaterAccountOrder = (accountIds: string[]) => {
  return protocol.post(URL_POST_SET_WATER_ACCOUNT_ORDER, {
    accountIds,
  })
}

export const postMergeWaterAccount = (payload: any) => {
  return protocol.post('/api/WaterAccount/Merge', payload)
}

export const deleteMergeWaterAccount = (mergeId: string) => {
  return protocol.delete(`/api/WaterAccount/Merge/${mergeId}`, {})
}

const URL_POST_MONTHLY_GOAL = '/api/waterAccount/setMonthlyGoal'
export const postMonthlyGoal = (payload: any) => {
  return protocol.post(URL_POST_MONTHLY_GOAL, payload)
}

const URL_POST_HOUSE_HOLD_PROFILE = '/api/waterAccount/setHouseHoldProfile'
export interface IHouseHouseProfileData {
  profileTypeId: string
  optionId: string | null
}
export const postHouseHoldProfile = (
  accountNumber: string,
  data: IHouseHouseProfileData[],
) => {
  return protocol.post(URL_POST_HOUSE_HOLD_PROFILE, { accountNumber, data })
}

const URL_POST_ACCOUNT_NAME = '/api/waterAccount/setAccountName'
export const postAccountName = (accountId: string, accountName: string) => {
  return protocol.post(URL_POST_ACCOUNT_NAME, {
    accountId,
    accountName,
  })
}

const URL_GET_ALL_WATER_ACCOUNT = '/api/waterAccount/getAllAccounts'
export const getAllAccounts = () => {
  return protocol.get(URL_GET_ALL_WATER_ACCOUNT)
}

const URL_POST_GET_ALL_PENDING_WATER_ACCOUNT =
  '/api/waterAccount/GetPendingWaterAccounts'
export const getAllPendingWaterAccount = () => {
  return protocol.post(URL_POST_GET_ALL_PENDING_WATER_ACCOUNT)
}

const URL_GET_HOUSE_HOLD_PROFILE = '/api/waterAccount/getHouseHoldProfile'
export const postGetHouseholdProfile = (accountNumber: string) => {
  return protocol.post(URL_GET_HOUSE_HOLD_PROFILE, {
    accountNumber,
  })
}

const URL_GET_WATER_ACCOUNTS = '/api/waterAccount/getWaterAccounts'
export const getWaterAccounts = (
  contractAccountNumbers: string[],
  mergeAccountIds: string[] = [],
) => {
  return protocol.post(URL_GET_WATER_ACCOUNTS, {
    contractAccountNumbers,
    mergeAccountIds,
  })
}

const URL_POST_MEMBERS_ACCESS_LIST = '/api/waterAccount/GetMembersAccessList'
export const postGetMembersAccessList = (
  payload: WaterAccountManagement.IMembersAccessListPayload,
) => {
  const { contractAccountNumber, page = 1, pageSize = 1000 } = payload
  return protocol.post(URL_POST_MEMBERS_ACCESS_LIST, {
    contractAccountNumber,
    page,
    pageSize,
  })
}

const URL_POST_REMOVE_MEMBER_ACCESS = '/api/waterAccount/RemoveAccess'
export const postRemoveMembersAccess = (requestId: string) => {
  return protocol.post(URL_POST_REMOVE_MEMBER_ACCESS, { requestId })
}

// water usage
export const postGetMeterDataByYear = (
  contractAccountNumbers: string[],
  date: Date,
) => {
  const startDate = dayjs(date)
    .startOf('years')
    .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
  const endDate = dayjs(date)
    .endOf('years')
    .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
  return postGetMetaData(
    contractAccountNumbers,
    startDate,
    endDate,
    TIME_FRAME.MONTHLY,
  )
}

export const postGetMeterDataByMonth = (
  contractAccountNumbers: string[],
  date: Date,
) => {
  const startDate = dayjs(date)
    .startOf('months')
    .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
  const endDate = dayjs(date)
    .endOf('months')
    .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
  return postGetMetaData(
    contractAccountNumbers,
    startDate,
    endDate,
    TIME_FRAME.DAILY,
  )
}

const URL_POST_GET_LAST_7_DAYS_METER_DATA =
  '/api/waterAccount/getLast7DaysMeterData'
export const postGetLast7DaysMeterData = (contractAccountNumbers: string[]) => {
  return protocol.post(URL_POST_GET_LAST_7_DAYS_METER_DATA, {
    contractAccountNumbers,
  })
}

export const postGetMeterDataByDay = (
  contractAccountNumbers: string[],
  date: Date,
) => {
  const startDate = dayjs(date)
    .startOf('days')
    .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
  const endDate = dayjs(date).endOf('days').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
  return postGetMetaData(
    contractAccountNumbers,
    startDate,
    endDate,
    TIME_FRAME.HOURLY,
  )
}

const URL_POST_GET_METER_DATA = '/api/waterAccount/GetMeterData'
export const postGetMetaData = (
  contractAccountNumbers: string[],
  startDate: string,
  endDate: string,
  timeFrame: TIME_FRAME,
) => {
  return protocol.post(URL_POST_GET_METER_DATA, {
    contractAccountNumbers,
    startDate,
    endDate,
    timeFrame,
  })
}

const URL_GET_NOTIFICATION_SETTING = '/api/waterAccount/getNotificationSetting'
export const postGetNotificationSetting = (accountNumber: string) => {
  return protocol.post(URL_GET_NOTIFICATION_SETTING, {
    accountNumber,
  })
}

const URL_POST_NOTIFICATION_SETTING = '/api/waterAccount/setNotificationSetting'
export const setNotificationSetting = (accountNumber: string, payload: any) => {
  return protocol.post(URL_POST_NOTIFICATION_SETTING, payload, {
    params: { accountNumber },
  })
}

const URL_DOWNLOAD_METER_DATA = '/api/waterAccount/DownloadMeterData'
const URL_DOWNLOAD_METER_DATA_BY_MERGED_ACCOUNT =
  '/api/waterAccount/DownloadMeterDataByMergeAccount'
export const postDownloadMeterData = (
  payload: WaterAccountManagement.IDownloadUsageData & {
    contractAccountNumber: string
    mergedWaterAccountUsersId: string
    isMerged: boolean
  },
) => {
  const {
    contractAccountNumber,
    mergedWaterAccountUsersId,
    isMerged = false,
    fromDate,
    toDate,
    unit,
    timeFrame,
    exportFormat,
  } = payload
  const timeUnit: dayjs.UnitType =
    timeFrame === TIME_FRAME.MONTHLY ? 'months' : 'days'
  const startDate = dayjs(fromDate)
    .startOf(timeUnit)
    .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
  const endDate = dayjs(toDate)
    .endOf(timeUnit)
    .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
  let params: any = {
    startDate,
    endDate,
    unit,
    timeFrame,
    exportFormat,
  }
  params = !isMerged
    ? { contractAccountNumber, ...params }
    : { mergedWaterAccountUsersId, ...params }

  return protocol
    .post(
      !isMerged
        ? URL_DOWNLOAD_METER_DATA
        : URL_DOWNLOAD_METER_DATA_BY_MERGED_ACCOUNT,
      params,
    )
    .then((resp) => {
      const { blob, fileName } = resp.data
      const blobData = Helpers.base64ToBlob(blob.fileContents, blob.contentType)
      const url = window.URL.createObjectURL(blobData)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      setTimeout(function () {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }, 200)
      return Promise.resolve(resp)
    })
    .catch((error) => Promise.reject(error))
}

const URL_GET_METER_ALERTS = '/api/waterAccount/getMeterAlerts'
export interface IMeterAlertsPayload {
  ContractAccountNumbers?: string[]
  AlarmTypes: LeakManagement.LeakStatusType[]
  Page: number
  PageSize: number
  IsActive: boolean
  states: LeakManagement.METER_ALERT_STATE[]
}
const toListModel = (
  resp: AxiosResponse<any, any>,
): AxiosResponse<any, any> => ({
  ...resp,
  data: {
    ...resp.data,
    meterAlerts: LodashUtils.map(
      resp.data.meterAlerts,
      (o) => new LeakManagement.MeterAlert({...o, alarmStartDate: LodashUtils.endsWith(o.alarmStartDate, "Z") ? o.alarmStartDate : `${o.alarmStartDate}Z`}),
    ),
  },
})
export const getMeterAlerts = (payload: IMeterAlertsPayload) => {
  return protocol
    .post(URL_GET_METER_ALERTS, payload)
    .then(toListModel)
    .catch((err) => err)
}

const URL_POST_ADD_LEAK_DETAIL = '/api/waterAccount/AddLeak'
export const addLeak = (payload: any) => {
  return protocol.post(URL_POST_ADD_LEAK_DETAIL, payload)
}

const URL_POST_UPDATE_LEAK_DETAIL = '/api/waterAccount/UpdateLeak'
export const updateLeak = (payload: any) => {
  return protocol.post(URL_POST_UPDATE_LEAK_DETAIL, payload)
}

const URL_POST_DELETE_PAST_LEAK_DETECTED_BY_USER =
  '/api/waterAccount/DeletePastLeak'
export const removePastLeakDetectedByUser = (payload: any) => {
  return protocol.post(URL_POST_DELETE_PAST_LEAK_DETECTED_BY_USER, payload)
}

const URL_POST_CHECK_MANUAL_ACCOUNT_ELIGIBILITY =
  '/api/waterAccount/CheckManualAccountEligibility'
export const CheckManualAccountEligibility = (payload: any) => {
  return protocol.post(URL_POST_CHECK_MANUAL_ACCOUNT_ELIGIBILITY, payload)
}

const URL_POST_GRANT_PERMISSION = '/api/waterAccount/GrantPermission'
export const grantPermission = (payload: any) => {
  return protocol.post(URL_POST_GRANT_PERMISSION, payload)
}

const URL_PUT_UPDATE_ACCESS = '/api/waterAccount/UpdateAccess'
export const updateMemberAccess = (payload: any) => {
  return protocol.put(URL_PUT_UPDATE_ACCESS, payload)
}

export const URL_POST_REQUESTOR_REPLY_REQUEST =
  '/api/waterAccount/RequestorReplyRequest'
export const requestorReplyGrantedAccessRequest = (payload: any) => {
  return protocol.post(URL_POST_REQUESTOR_REPLY_REQUEST, payload)
}

export const URL_GET_PREDEFINED_VALUE_REQUEST = '/api/predefined-value/list'

export const getPredefinedValues = (payload: any) => {
  return protocol.get(URL_GET_PREDEFINED_VALUE_REQUEST, { params: payload })
}
export const URL_GET_NEW_ELIGIBLE_WATER_ACCOUNT =
  '/api/waterAccount/GetNewEligibleWaterAccount'
export const getNewEligibleWaterAccount = () => {
  return protocol.get(URL_GET_NEW_ELIGIBLE_WATER_ACCOUNT)
}

export const URL_DONT_SHOW_REMIND_NEW_ELIGIBLE_WATER_ACCOUNT =
  '/api/waterAccount/DontShowNewEligibleWaRemind'
export const dontShowRemindNewEligibleWaterAccount = (payload: any) => {
  return protocol.post(URL_DONT_SHOW_REMIND_NEW_ELIGIBLE_WATER_ACCOUNT, payload)
}

export const URL_DONT_SHOW_REMIND_TERMINATED_WATER_ACCOUNT =
  '/api/waterAccount/DontShowTerminatedRemind'
export const dontShowRemindTerminatedWaterAccount = (payload: any) => {
  return protocol.post(URL_DONT_SHOW_REMIND_TERMINATED_WATER_ACCOUNT, payload)
}

export const URL_ADD_NEW_ELIGIBLE_WATER_ACCOUNT =
  '/api/waterAccount/AddNewWaterAccounts'
export const addNewEligibleWaterAccount = (payload: any) => {
  return protocol.post(URL_ADD_NEW_ELIGIBLE_WATER_ACCOUNT, payload)
}
